<script setup>
    import { onMounted, computed, watch, ref } from 'vue';
    import { useRouter } from 'vue-router';
    import { useSqlEditorStore } from '@/stores/sqlEditorStore.ts';
    import { getSqlEditorLink } from '@/shared/helpers/routing';
    import SqlEditControl from '@/shared/components/SqlEditControl.vue';
    import SqlEditorNotification from './SqlEditorNotification.vue';
    import SqlLibraryBrowser from './SqlLibraryBrowser.vue';
    import CopyLinkButton from './CopyLinkButton.vue';
    import DeleteButton from './DeleteButton.vue';
    import ExportButton from './ExportButton.vue';
    import QueryResult from './QueryResult.vue';
    import SaveButton from './SaveButton.vue';
    import LoadButton from './LoadButton.vue';
    import RunButton from './RunButton.vue';
    import SqlParameters from './SqlParameters.vue';
    import QueryUsageInfo from './QueryUsageInfo.vue';

    const router = useRouter();

    const showUsage = ref(false);

    const props = defineProps({
        requestedLibrary: {
            type: String,
            default: null
        },
        requestedSavedQueryGuid: {
            type: String,
            default: null
        }
    });

    const store = useSqlEditorStore();

    const savedQueryLabel = computed(() => {
        if (store.loadedQueryOriginalIsPublic) {
            if (store.loadedQueryOwnedByCurrentUser) {
                return `${store.loadedQueryOriginalName} (public, owned by you)`;
            } else {
                return `${store.loadedQueryOriginalName} (public)`;
            }
        } else {
            return store.loadedQueryOriginalName;
        }
    });

    function onSetSql(sql) {
        store.loadedQueryUnset();
        store.loadedQuerySql = sql;
    }

    async function onUnloadQuery() {
        store.loadedQueryUnset();
        await router.push(getSqlEditorLink());
    }

    watch(
        props,
        () => {
            if (props.requestedSavedQueryGuid) {
                store.loadedQuerySet(props.requestedSavedQueryGuid);
            } else if (props.requestedLibrary) {
                store.librarySetSelected(props.requestedLibrary);
            }
        },
        { immediate: true }
    );

    onMounted(async () => {
        await store.init();
    });
</script>

<template>
    <div>
        <div class="columns">
            <div
                class="column is-3">
                <h1 class="title">
                    <b-icon
                        icon="table-search"
                        size="" />
                    SQL Editor
                </h1>
                <sql-library-browser
                    @set-sql="onSetSql" />
            </div>
            <div class="column is-9">
                <div class="buttons">
                    <run-button />
                    <load-button />
                    <save-button />
                    <delete-button />
                    <copy-link-button />
                    <export-button />
                    <b-button
                        icon-left="file-document-outline"
                        :active="showUsage"
                        :disabled="!store.queryIsLoaded"
                        @click="showUsage = !showUsage">
                        <span>
                            Governance
                            <b-tag
                                v-if="store.loadedQueryUsedByExternalApplication"
                                type="is-info"
                                rounded>!!</b-tag>
                        </span>
                    </b-button>
                </div>
                <div
                    v-if="store.queryIsLoaded"
                    class="has-text-grey mb-3">
                    Saved query: {{ savedQueryLabel }}
                    <b-icon
                        icon="close-circle"
                        size="is-small"
                        title="Unload query"
                        class="is-clickable"
                        @click="onUnloadQuery" />
                    <br>
                    Guid: {{ store.loadedQueryGuid }}
                    <span v-if="store.loadedQueryUserName">
                        by {{ store.loadedQueryUserName }}
                    </span>
                    <span v-if="store.loadedQueryIsPublic">
                        (public)
                    </span>
                    <span v-if="store.loadedQueryIsShared">
                        (shared)
                    </span>
                </div>
                <query-usage-info
                    :show="showUsage"
                    @close="showUsage = false" />
                <div class="columns">
                    <div
                        class="column"
                        style="overflow:auto">
                        <sql-edit-control
                            v-model="store.loadedQuerySql"
                            enable-run-shortcut
                            @run="store.runQuery()" />
                    </div>
                    <div
                        v-if="store.loadedQueryParameters.length > 0"
                        class="column is-one-quarter">
                        <sql-parameters />
                    </div>
                </div>
                <sql-editor-notification />
                <hr class="mb-1 mt-0">
                <query-result />
            </div>
        </div>
    </div>
</template>

<style>
.dropdown-content {
    padding-bottom: 0 !important;
}
</style>
